import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import BookSearchPreview from './BookSearchPreview'
import StarRating from './StarRating'
import { useBookSearch, BookSearchResult } from '../hooks/useBookSearch'
import Modal from './Modal'

interface AddBookProps {
  onClose: () => void
  onSuccess: () => void
  defaultStatus?: BookStatus
  userProvider?: string | null
  onProviderSelected?: (provider: string) => void
}

interface Club {
  id: number
  name: string
}

type BookStatus = 'want_to_read' | 'currently_reading' | 'past_read' | null

export default function AddBook({ onClose, onSuccess, defaultStatus, userProvider, onProviderSelected }: AddBookProps) {
  const { searchQuery, setSearchQuery, results, loading, error: searchError } = useBookSearch()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [previewBook, setPreviewBook] = useState<BookSearchResult | null>(null)
  const [selectedBook, setSelectedBook] = useState<BookSearchResult | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<BookStatus>(defaultStatus || null)

  // Want to Read fields
  const [notes, setNotes] = useState('')

  // Currently Reading fields
  const [clubs, setClubs] = useState<Club[]>([])
  const [selectedClubId, setSelectedClubId] = useState<number | 'personal' | null>(null)
  const [startedAt, setStartedAt] = useState(new Date().toISOString().split('T')[0])

  // Past Read fields
  const [pastReadStartedAt, setPastReadStartedAt] = useState('')
  const [completedAt, setCompletedAt] = useState(new Date().toISOString().split('T')[0])
  const [rating, setRating] = useState<number | null>(null)
  const [reviewText, setReviewText] = useState('')

  // Manual entry fields
  const [manualMode, setManualMode] = useState(false)
  const [manualBook, setManualBook] = useState({
    title: '',
    author: '',
    isbn: '',
    number_of_pages: ''
  })

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [results])

  // Fetch clubs when user selects "Currently Reading"
  useEffect(() => {
    if (selectedStatus === 'currently_reading') {
      fetchClubs()
    }
  }, [selectedStatus])

  // Set error from search hook
  useEffect(() => {
    if (searchError) {
      setError(searchError)
    }
  }, [searchError])

  const fetchClubs = async () => {
    try {
      const response = await fetch('/api/v1/clubs')
      if (response.ok) {
        const data = await response.json()
        setClubs(data.clubs)
        if (data.clubs.length === 0) {
          setSelectedClubId('personal')
        }
      }
    } catch (err) {
      console.error('Failed to fetch clubs:', err)
    }
  }

  const handleSelectBook = (book: BookSearchResult) => {
    setSelectedBook(book)
    setSearchQuery('')
    // If we have a default status, auto-select it when a book is chosen
    if (defaultStatus) {
      setSelectedStatus(defaultStatus)
    }
  }

  const getBookData = () => {
    if (manualMode) {
      if (!manualBook.title || !manualBook.author) {
        throw new Error('Title and author are required')
      }
      return {
        title: manualBook.title,
        author: manualBook.author,
        isbn: manualBook.isbn || undefined,
        number_of_pages: manualBook.number_of_pages ? parseInt(manualBook.number_of_pages) : undefined
      }
    } else {
      if (!selectedBook) {
        throw new Error('Please select a book')
      }
      return {
        title: selectedBook.title,
        author: selectedBook.author,
        isbn: selectedBook.isbn,
        description: selectedBook.description,
        cover_url: selectedBook.cover_url,
        first_publish_year: selectedBook.first_publish_year,
        number_of_pages: selectedBook.number_of_pages,
        metadata: selectedBook.metadata
      }
    }
  }

  const handleSubmit = async () => {
    if (!selectedStatus) {
      setError('Please select a status')
      return
    }

    if (!manualMode && !selectedBook) {
      setError('Please select a book')
      return
    }

    if (manualMode && (!manualBook.title || !manualBook.author)) {
      setError('Title and author are required')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      if (selectedStatus === 'want_to_read') {
        await submitWantToRead()
      } else if (selectedStatus === 'currently_reading') {
        await submitCurrentlyReading()
      } else if (selectedStatus === 'past_read') {
        await submitPastRead()
      }

      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to add book')
    } finally {
      setSubmitting(false)
    }
  }

  const submitWantToRead = async () => {
    const bookData = getBookData()
    const response = await fetchWithCsrf('/api/v1/to_read_books', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to_read_book: {
          notes: notes,
          book_attributes: bookData
        }
      })
    })

    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.errors?.join(', ') || 'Failed to add book')
    }
  }

  const submitCurrentlyReading = async () => {
    if (!selectedClubId) {
      throw new Error('Please select where you are reading this book')
    }

    if (!startedAt) {
      throw new Error('Please enter when you started reading')
    }

    const bookData = getBookData()
    const url = selectedClubId === 'personal'
      ? '/api/v1/personal_readings'
      : `/api/v1/clubs/${selectedClubId}/book_picks`

    const bookPickResponse = await fetchWithCsrf(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        book_pick: {
          status: 'current',
          started_at: startedAt,
          completed_at: null,
          is_historical: selectedClubId !== 'personal',
          unclaimed: false,
          book_attributes: bookData
        }
      })
    })

    if (!bookPickResponse.ok) {
      const data = await bookPickResponse.json()
      throw new Error(data.errors?.join(', ') || 'Failed to start reading')
    }

    const bookPickData = await bookPickResponse.json()
    const bookPickId = bookPickData.book_pick.id

    // Mark as reading (start progress tracking)
    const progressResponse = await fetchWithCsrf(`/api/v1/book_picks/${bookPickId}/progress`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action_type: 'start_reading'
      })
    })

    if (!progressResponse.ok) {
      throw new Error('Failed to start progress tracking')
    }
  }

  const submitPastRead = async () => {
    if (!completedAt) {
      throw new Error('Please enter when you completed the book')
    }

    const bookData = getBookData()
    const response = await fetchWithCsrf('/api/v1/personal_readings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        book_pick: {
          status: 'completed',
          started_at: pastReadStartedAt || null,
          completed_at: completedAt,
          book_attributes: bookData
        }
      })
    })

    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.errors?.join(', ') || 'Failed to add past read')
    }

    // If there's a rating or review, submit that too
    if (rating || reviewText) {
      const bookPickData = await response.json()
      const bookPickId = bookPickData.book_pick.id

      const reviewResponse = await fetchWithCsrf('/api/v1/book_reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_review: {
            book_pick_id: bookPickId,
            rating: rating,
            review_text: reviewText
          }
        })
      })

      if (!reviewResponse.ok) {
        console.error('Failed to add review, but book was added')
      }
    }
  }

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        size="lg"
        showCloseButton={true}
        maxHeight="90vh"
        className="flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Add a Book</h2>
            <p className="text-gray-600">Search for a book and choose how to add it</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {!selectedBook && !manualMode ? (
            <>
              {/* Search Input */}
              <div className="mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title or author..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  autoFocus
                />
              </div>

              {/* Manual Entry Link */}
              <div className="mb-6 text-center">
                <button
                  onClick={() => setManualMode(true)}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Can't find it? Add manually
                </button>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              )}

              {/* Search Results */}
              {!loading && results.length > 0 && (
                <div className="space-y-3">
                  {results.map((book, index) => (
                    <button
                      key={index}
                      onClick={() => setPreviewBook(book)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full bg-white border-2 rounded-xl p-4 transition-all text-left ${
                        highlightedIndex === index
                          ? 'border-indigo-500 shadow-md bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-400 hover:shadow-md'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row gap-4">
                        {book.cover_url ? (
                          <img
                            src={book.cover_url}
                            alt={book.title}
                            className="w-16 h-24 object-cover rounded shadow-sm"
                          />
                        ) : (
                          <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                            {book.first_publish_year && <span>{book.first_publish_year}</span>}
                            {book.number_of_pages && (
                              <>
                                {book.first_publish_year && <span>•</span>}
                                <span>{book.number_of_pages} pages</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-indigo-600 mt-1">Click to see details</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!loading && searchQuery.length >= 2 && results.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-600">No books found</p>
                  <p className="text-sm text-gray-500 mt-1">Try a different search term</p>
                </div>
              )}

              {!loading && searchQuery.length < 2 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-gray-600">Start typing to search for books</p>
                </div>
              )}
            </>
          ) : manualMode && !selectedStatus ? (
            <>
              {/* Manual Entry Form */}
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add Book Manually</h3>
                  <button
                    onClick={() => setManualMode(false)}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Back to Search
                  </button>
                </div>

                <div>
                  <label htmlFor="manualTitle" className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    id="manualTitle"
                    type="text"
                    value={manualBook.title}
                    onChange={(e) => setManualBook({ ...manualBook, title: e.target.value })}
                    placeholder="Enter book title"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="manualAuthor" className="block text-sm font-medium text-gray-700 mb-2">
                    Author *
                  </label>
                  <input
                    id="manualAuthor"
                    type="text"
                    value={manualBook.author}
                    onChange={(e) => setManualBook({ ...manualBook, author: e.target.value })}
                    placeholder="Enter author name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="manualIsbn" className="block text-sm font-medium text-gray-700 mb-2">
                      ISBN (Optional)
                    </label>
                    <input
                      id="manualIsbn"
                      type="text"
                      value={manualBook.isbn}
                      onChange={(e) => setManualBook({ ...manualBook, isbn: e.target.value })}
                      placeholder="e.g., 978-0-12-345678-9"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="manualPages" className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Pages (Optional)
                    </label>
                    <input
                      id="manualPages"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={manualBook.number_of_pages}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '')
                        setManualBook({ ...manualBook, number_of_pages: value })
                      }}
                      placeholder="e.g., 350"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                  </div>
                </div>

                {defaultStatus ? (
                  <button
                    onClick={() => setSelectedStatus(defaultStatus)}
                    disabled={!manualBook.title || !manualBook.author}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                  >
                    Continue
                  </button>
                ) : (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">How would you like to add this book?</h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => setSelectedStatus('want_to_read')}
                        disabled={!manualBook.title || !manualBook.author}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                          </svg>
                          <div>
                            <h4 className="font-semibold text-gray-900 group-hover:text-indigo-600">Want to Read</h4>
                            <p className="text-sm text-gray-600">Add to your reading list</p>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setSelectedStatus('currently_reading')}
                        disabled={!manualBook.title || !manualBook.author}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <div>
                            <h4 className="font-semibold text-gray-900 group-hover:text-green-600">Currently Reading</h4>
                            <p className="text-sm text-gray-600">Start reading this book now</p>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setSelectedStatus('past_read')}
                        disabled={!manualBook.title || !manualBook.author}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <h4 className="font-semibold text-gray-900 group-hover:text-purple-600">Already Read</h4>
                            <p className="text-sm text-gray-600">Add a book you've finished</p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : !selectedStatus ? (
            <>
              {/* Selected Book - Choose Status */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex gap-4">
                    {selectedBook.cover_url ? (
                      <img
                        src={selectedBook.cover_url}
                        alt={selectedBook.title}
                        className="w-20 h-30 object-cover rounded shadow-sm"
                      />
                    ) : (
                      <div className="w-20 h-30 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{selectedBook.title}</h3>
                      <p className="text-sm text-gray-600">by {selectedBook.author}</p>
                    </div>
                    <button
                      onClick={() => setSelectedBook(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">How would you like to add this book?</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setSelectedStatus('want_to_read')}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left group"
                    >
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-gray-900 group-hover:text-indigo-600">Want to Read</h4>
                          <p className="text-sm text-gray-600">Add to your reading list</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setSelectedStatus('currently_reading')}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
                    >
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-gray-900 group-hover:text-green-600">Currently Reading</h4>
                          <p className="text-sm text-gray-600">Start reading this book now</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setSelectedStatus('past_read')}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
                    >
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-gray-900 group-hover:text-purple-600">Already Read</h4>
                          <p className="text-sm text-gray-600">Add a book you've finished</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Status-specific forms */}
              <div className="space-y-4">
                {/* Book Summary */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex gap-4">
                    {!manualMode && selectedBook?.cover_url ? (
                      <img
                        src={selectedBook.cover_url}
                        alt={selectedBook.title}
                        className="w-16 h-24 object-cover rounded shadow-sm"
                      />
                    ) : (
                      <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1 text-sm">
                        {manualMode ? manualBook.title : selectedBook?.title}
                      </h3>
                      <p className="text-xs text-gray-600">
                        by {manualMode ? manualBook.author : selectedBook?.author}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedStatus(null)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Change status"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Want to Read Form */}
                {selectedStatus === 'want_to_read' && (
                  <>
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Why do you want to read this book?"
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none resize-none"
                      />
                    </div>
                  </>
                )}

                {/* Currently Reading Form */}
                {selectedStatus === 'currently_reading' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Where are you reading this?
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-indigo-500 transition-all">
                          <input
                            type="radio"
                            name="club"
                            value="personal"
                            checked={selectedClubId === 'personal'}
                            onChange={() => setSelectedClubId('personal')}
                            className="mr-3"
                          />
                          <span className="text-sm font-medium text-gray-900">Personal Reading</span>
                        </label>
                        {clubs.map((club) => (
                          <label key={club.id} className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-indigo-500 transition-all">
                            <input
                              type="radio"
                              name="club"
                              value={club.id}
                              checked={selectedClubId === club.id}
                              onChange={() => setSelectedClubId(club.id)}
                              className="mr-3"
                            />
                            <span className="text-sm font-medium text-gray-900">{club.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="startedAt" className="block text-sm font-medium text-gray-700 mb-2">
                        When did you start?
                      </label>
                      <input
                        id="startedAt"
                        type="date"
                        value={startedAt}
                        onChange={(e) => setStartedAt(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      />
                    </div>
                  </>
                )}

                {/* Past Read Form */}
                {selectedStatus === 'past_read' && (
                  <>
                    <div>
                      <label htmlFor="pastReadStartedAt" className="block text-sm font-medium text-gray-700 mb-2">
                        When did you start? (Optional)
                      </label>
                      <input
                        id="pastReadStartedAt"
                        type="date"
                        value={pastReadStartedAt}
                        onChange={(e) => setPastReadStartedAt(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label htmlFor="completedAt" className="block text-sm font-medium text-gray-700 mb-2">
                        When did you finish?
                      </label>
                      <input
                        id="completedAt"
                        type="date"
                        value={completedAt}
                        onChange={(e) => setCompletedAt(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rating (Optional)
                      </label>
                      <StarRating
                        rating={rating || 0}
                        interactive={true}
                        onChange={setRating}
                        size="lg"
                      />
                    </div>

                    <div>
                      <label htmlFor="reviewText" className="block text-sm font-medium text-gray-700 mb-2">
                        Review (Optional)
                      </label>
                      <textarea
                        id="reviewText"
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        placeholder="What did you think of this book?"
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none resize-none"
                      />
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                >
                  {submitting ? 'Adding...' : 'Add Book'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => {
            handleSelectBook(previewBook)
            setPreviewBook(null)
          }}
          userProvider={userProvider}
          onProviderSelected={onProviderSelected}
          showSelectButton={true}
        />
      )}
    </>
  )
}
